export const META_RATE_LIMITED_CODE = "META_RATE_LIMITED";

const META_THROTTLE_CODES = new Set([4, 17, 32, 613, 80004, 80007]);

const RAW_THROTTLE_TEXT =
  /application request limit reached|user request limit reached|calls to this api have exceeded|request limit reached|\(#4\)|\(#17\)|\(#613\)|\(#80004\)|\(#80007\)/i;

export interface MetaErrorLike {
  statusCode?: unknown;
  status?: unknown;
  code?: unknown;
  errorCode?: unknown;
  message?: unknown;
  error?: unknown;
  retryAfter?: unknown;
  retry_after?: unknown;
  meta?: unknown;
  details?: unknown;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function textOf(err: unknown): string {
  if (typeof err === "string") return err;
  const rec = asRecord(err);
  if (!rec) return "";
  const parts: unknown[] = [rec.message, rec.error];
  const nested = asRecord(rec.error);
  if (nested) parts.push(nested.message, nested.error);
  const details = asRecord(rec.details);
  if (details) parts.push(details.message, details.error);
  return parts.filter((p) => typeof p === "string").join(" ");
}

export function statusOf(err: unknown): number | null {
  const rec = asRecord(err);
  if (!rec) return null;
  const raw = rec.statusCode ?? rec.status;
  const n = typeof raw === "string" ? Number(raw) : raw;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

function machineCodes(err: unknown): string[] {
  const rec = asRecord(err);
  if (!rec) return [];
  const nested = asRecord(rec.error);
  const raw = [rec.code, rec.errorCode, nested?.code];
  return raw
    .filter((c) => typeof c === "string" || typeof c === "number")
    .map((c) => String(c).trim().toLowerCase())
    .filter(Boolean);
}

export function isMetaRateLimitError(err: unknown): boolean {
  if (!err) return false;

  if (statusOf(err) === 429) return true;

  for (const code of machineCodes(err)) {
    if (code === META_RATE_LIMITED_CODE.toLowerCase()) return true;
    if (code === "rate_limited" || code === "rate_limit_exceeded") return true;
    const n = Number(code);
    if (Number.isFinite(n) && META_THROTTLE_CODES.has(n)) return true;
  }

  return RAW_THROTTLE_TEXT.test(textOf(err));
}

const ONE_HOUR_SECONDS = 3600;

export function retryAfterSecondsFrom(err: unknown): number | null {
  const rec = asRecord(err);
  if (!rec) return null;

  const meta = asRecord(rec.meta);
  const details = asRecord(rec.details);
  const nested = asRecord(rec.error);
  const nestedMeta = asRecord(nested?.meta);

  const candidates: unknown[] = [
    rec.retryAfter,
    rec.retry_after,
    meta?.retryAfter,
    meta?.retry_after,
    meta?.retry_after_seconds,
    details?.retryAfter,
    details?.retry_after,
    nested?.retryAfter,
    nested?.retry_after,
    nestedMeta?.retryAfter,
    nestedMeta?.retry_after,
  ];

  for (const raw of candidates) {
    if (typeof raw !== "number" && typeof raw !== "string") continue;
    if (typeof raw === "string" && raw.trim() === "") continue;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) continue;
    return Math.min(Math.ceil(n), ONE_HOUR_SECONDS);
  }

  return null;
}

export function formatRetryAfter(seconds: number | null | undefined): string | null {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds < 60) return "less than a minute";
  const minutes = Math.ceil(seconds / 60);
  if (minutes === 1) return "about a minute";
  if (minutes < 60) return `about ${minutes} minutes`;
  return "about an hour";
}

export interface MetaRateLimitNotice {
  title: string;
  description: string;
  retryAfterSeconds: number | null;
}

export function metaRateLimitNotice(err: unknown): MetaRateLimitNotice {
  const retryAfterSeconds = retryAfterSecondsFrom(err);
  const wait = formatRetryAfter(retryAfterSeconds);
  return {
    title: "Facebook data is temporarily unavailable",
    description: wait
      ? `This workspace has reached Meta's hourly limit for Facebook API requests. It resets automatically — try again in ${wait}.`
      : "This workspace has reached Meta's hourly limit for Facebook API requests. It resets automatically within the hour — please try again shortly.",
    retryAfterSeconds,
  };
}

export function shouldRetryMetaQuery(
  failureCount: number,
  err: unknown,
  maxRetries = 1
): boolean {
  if (isMetaRateLimitError(err)) return false;
  const status = statusOf(err);
  if (status !== null && status >= 400 && status < 500) return false;
  return failureCount < maxRetries;
}
